import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';

interface NoteDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message?: string;
  confirmText: string;
  isLoading?: boolean;
  onConfirm: (note: string) => void;
}

/**
 * Every fund-management change requires a mandatory audit note ("הערות").
 * The note is appended to the fund's audit trail on the server.
 */
const NoteDialog = ({ open, onClose, title, message, confirmText, isLoading = false, onConfirm }: NoteDialogProps) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [showRequiredError, setShowRequiredError] = useState(false);

  useEffect(() => {
    if (open) {
      setNote('');
      setShowRequiredError(false);
    }
  }, [open]);

  const handleConfirm = () => {
    if (!note.trim()) {
      setShowRequiredError(true);
      return;
    }
    onConfirm(note.trim());
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }} title={title}>
      <div className="flex flex-col gap-3">
        {message && <p className="body-md-regular text-(--font-primary)">{message}</p>}
        <label className="body-md-semibold" htmlFor="fund-note-input">{t('fundsManagement.noteDialog.noteLabel')}</label>
        <textarea
          id="fund-note-input"
          className="border border-[#e2e8f0] rounded-lg p-3 min-h-[100px] body-md-regular"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (e.target.value.trim()) setShowRequiredError(false);
          }}
          placeholder={t('fundsManagement.noteDialog.notePlaceholder')}
        />
        {showRequiredError && (
          <p className="body-md-medium text-red-600">{t('fundsManagement.noteDialog.noteRequired')}</p>
        )}
        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={onClose} disabled={isLoading}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t('loading') : confirmText}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default NoteDialog;
